import { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Avatar,
  IconButton,
  toast,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  Button,
  Dialog,
  DialogContent,
  Heading,
  Input,
  FormLabel
} from '@sparrowengg/twigs-react';
import { SingleAppProps } from '../types';
import { useNavigate } from 'react-router-dom';
import {
  LockIcon,
  EllipsisVerticalIcon,
  DocumentClipIcon,
  WrenchIcon,
  CloseIcon
} from '@sparrowengg/twigs-react-icons';
import CustomPill from 'commons/components/custom-pill';
import SparklesIcon from 'commons/icons/sparkles';
import { truncateText } from 'commons/helper';
import { appTypeConstants } from 'features/apps/add-apps/constants';
import { devPortalServices } from 'commons/services';

const SingleApp = ({ app, onDeleteSuccess }: SingleAppProps) => {
  const navigate = useNavigate();
  const isDevelopmentStatus = app?.appType === appTypeConstants.DEVELOPMENT;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteBtnLoader, setDeleteBtnLoader] = useState(false);

  const handleDeleteApp = async () => {
    setDeleteBtnLoader(true);
    try {
      if (isDevelopmentStatus) {
        await devPortalServices.deleteLocalDevApp(app?.appId as number);
      } else {
        await devPortalServices.deleteSingleApp(app?.appId as number);
      }
      toast({
        variant: 'default',
        description: `${app?.name || 'App'} has been deleted successfully.`
      });
      setShowDeleteModal(false);
      onDeleteSuccess?.();
    } catch (err) {
      console.error(err);
      toast({
        variant: 'error',
        description: 'Failed to delete app. Please try again.'
      });
    } finally {
      setDeleteBtnLoader(false);
    }
  };

  const isPublicApp = app?.appType === appTypeConstants.PUBLIC;
  const appType =
    app?.appType === appTypeConstants.DEAL
      ? appTypeConstants.PRIVATE
      : app?.appType ?? '';
  const appStatusLabel = isDevelopmentStatus
    ? 'Dev Mode'
    : app?.appStatus || '';
  const releaseRequestStatusDisplay =
    app?.status ?? app?.releaseRequestStatus ?? '';

  const typePillVariant = isPublicApp
    ? 'accent'
    : isDevelopmentStatus
      ? 'info'
      : 'warning';

  return (
    <Flex
      flexDirection="column"
      css={{
        width: 322,
        minHeight: 252,
        border: '$borderWidths$xs solid $neutral100',
        borderRadius: '$xl',
        background: '$white900',
        boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 10px 26px rgba(15, 23, 42, 0.1)'
        }
      }}
    >
      <Flex
        flexDirection="column"
        css={{
          padding: '$10',
          paddingBottom: '$4'
        }}
      >
        <Flex justifyContent="space-between" alignItems="flex-start">
          <Avatar size="2xl" src={app?.icon} rounded="md" />
          <Flex alignItems="center" gap="$3">
            <CustomPill
              label={
                appType?.charAt(0).toUpperCase() +
                appType?.slice(1)?.toLowerCase()
              }
              variant={typePillVariant}
              radius="md"
              isClosable={false}
              icon={
                isPublicApp ? (
                  <SparklesIcon />
                ) : isDevelopmentStatus ? (
                  <WrenchIcon size={16} color="#1E62D0" />
                ) : (
                  <LockIcon size={16} color="#B47409" />
                )
              }
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton
                  size="sm"
                  variant="ghost"
                  color="default"
                  icon={<EllipsisVerticalIcon />}
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                css={{
                  minWidth: '180px !important',
                  padding: '4px !important',
                  '& [role="menuitem"]': {
                    fontSize: '14px !important',
                    padding: '8px 14px !important',
                    lineHeight: '1.4 !important',
                    minHeight: 'unset !important'
                  }
                }}
              >
                {isDevelopmentStatus ? (
                  <>
                    <DropdownMenuItem
                      onClick={() =>
                        navigate(`/edit-local-dev-app/${app?.appId}`)
                      }
                    >
                      Edit App
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        navigate(
                          `/edit-local-dev-app/${app?.appId}/appFeatureUsage`
                        )
                      }
                    >
                      App Feature Usage
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteModal(true);
                      }}
                    >
                      Delete App
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem
                      onClick={() => {
                        const query = new URLSearchParams({
                          appId: String(app?.appId || ''),
                          appName: app?.name || ''
                        });
                        navigate(`/app-release-requests?${query.toString()}`);
                      }}
                    >
                      Release Request
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        navigate(`/edit-app/${app?.appId}?action=edit-info`)
                      }
                    >
                      Edit Info
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteModal(true);
                      }}
                    >
                      Delete App
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </Flex>
        </Flex>
        <Flex
          flexDirection="column"
          gap="$1"
          css={{
            marginTop: '$6',
            width: '100%',
            wordBreak: 'break-word',
            textWrap: 'wrap'
          }}
        >
          <Text size="md" weight="bold" css={{ whiteSpace: 'break-spaces' }}>
            {truncateText(app.name, 40)}
          </Text>
          <Text size="sm" css={{ color: '$neutral800' }}>
            {truncateText(app.shortDescription, 70)}
          </Text>
        </Flex>
      </Flex>
      <Box
        css={{
          flexShrink: 0,
          marginTop: 'auto',
          padding: '$10',
          paddingTop: '$6'
        }}
      >
        <Flex
          flexDirection="column"
          alignItems="flex-start"
          gap="$3"
          css={{ width: '100%' }}
        >
          {!isDevelopmentStatus ? (
            <Text size="xs" css={{ color: '$neutral700' }}>
              <Text as="span" weight="bold" css={{ color: '$neutral800' }}>
                Status:{' '}
              </Text>
              {releaseRequestStatusDisplay.trim()
                ? releaseRequestStatusDisplay
                : 'LIVE'}
            </Text>
          ) : null}
          <Flex
            alignItems="center"
            gap="$4"
            justifyContent="flex-start"
            css={{ flexWrap: 'wrap' }}
          >
            <CustomPill
              label={appStatusLabel}
              radius="md"
              isClosable={false}
              variant={isDevelopmentStatus ? 'info' : 'success'}
            />
            {!!app.incomingWebhookURL ? (
              <Tooltip content="Copy webhook url" size="sm">
                <IconButton
                  size="xs"
                  variant="ghost"
                  color="default"
                  icon={<DocumentClipIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(app.incomingWebhookURL);
                    toast({
                      variant: 'success',
                      description:
                        'Webhook URL has been copied to the clipboard.'
                    });
                  }}
                />
              </Tooltip>
            ) : null}
          </Flex>
        </Flex>
      </Box>
      {showDeleteModal && (
        <DeleteAppModal
          resetModal={() => setShowDeleteModal(false)}
          btnLoader={deleteBtnLoader}
          onClickHandler={handleDeleteApp}
          appName={app?.name ?? ''}
        />
      )}
    </Flex>
  );
};

const DeleteAppModal = ({
  resetModal,
  btnLoader,
  onClickHandler,
  appName
}: {
  resetModal: () => void;
  btnLoader?: boolean;
  onClickHandler: () => void;
  appName: string;
}) => {
  const [isDisabled, setIsDisabled] = useState(true);

  return (
    <Dialog open>
      <DialogContent css={{ padding: 0, borderRadius: '$xl', width: 476 }}>
        <Flex
          css={{
            padding: '$10 $12',
            borderBottom: '$borderWidths$xs solid $neutral200'
          }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Heading size="h6">App Deletion Confirmation</Heading>
          <IconButton
            size="md"
            variant="ghost"
            color="default"
            icon={<CloseIcon />}
            onClick={() => resetModal()}
          />
        </Flex>
        <Flex flexDirection="column" gap="$14" css={{ padding: '$12' }}>
          <Flex flexDirection="column" gap="$4">
            <Text size="sm">
              To delete, type the app name{' '}
              <Text
                as="span"
                size="sm"
                css={{ color: '$secondary400', whiteSpace: 'break-spaces' }}
                weight="bold"
              >
                {appName}
              </Text>
            </Text>
            <Input
              size="lg"
              onChange={(e) => {
                e.preventDefault();
                const inputValue = e?.currentTarget?.value?.trim() || '';
                const appNameNormalized = appName?.trim() || '';
                if (inputValue === appNameNormalized) {
                  setIsDisabled(false);
                } else {
                  setIsDisabled(true);
                }
              }}
            />
            <FormLabel>The app name must be case-sensitive</FormLabel>
          </Flex>
          <Text size="md">
            <Box as="strong">Note:</Box> The deletion will remove all the App
            history and it can't be revived.
          </Text>
          <Text size="md">Are you sure you want to delete the app?</Text>
        </Flex>
        <Flex
          alignItems="center"
          gap="$6"
          css={{
            padding: '$12',
            paddingTop: 0,
            button: {
              flex: 1
            }
          }}
        >
          <Button
            color="error"
            size="xl"
            onClick={() => resetModal()}
            disabled={btnLoader}
          >
            No, Go Back
          </Button>
          <Button
            color="secondary"
            size="xl"
            onClick={() => onClickHandler()}
            disabled={isDisabled || btnLoader}
            loading={btnLoader}
          >
            Yes, I am sure
          </Button>
        </Flex>
      </DialogContent>
    </Dialog>
  );
};

export default SingleApp;
